import { RefObject, useCallback } from 'react';
import { useKeyboardShortcut } from './useKeyboardShortcut';
import { isEditableElement } from '../../utils/shortcuts/isEditableElement';

type UseCopyPasteLayoutObjectShortcutsArgs = {
  // DOM root the shortcut dispatcher uses to decide the editor workspace owns the keydown.
  scopeRef: RefObject<HTMLElement | null>;
  selectedIndex: number | null;
  onCopy: () => void;
  onPaste: () => void;
};

// Ctrl/Cmd+C copies the selected canvas object into the editor clipboard,
// Ctrl/Cmd+V pastes its clone. Ignored while an editable element has focus
// (the event's document — works in the PiP window too), so the native
// copy/paste inside the panel inputs stays untouched.
export const useCopyPasteLayoutObjectShortcuts = ({
  scopeRef,
  selectedIndex,
  onCopy,
  onPaste,
}: UseCopyPasteLayoutObjectShortcutsArgs) => {
  const shouldHandleCopy = useCallback(
    (event: KeyboardEvent) => {
      const eventDocument =
        (event.target as Node | null)?.ownerDocument ?? document;

      return (
        (event.ctrlKey || event.metaKey) &&
        event.key.toLowerCase() === 'c' &&
        selectedIndex !== null &&
        !isEditableElement(eventDocument.activeElement)
      );
    },
    [selectedIndex]
  );

  const shouldHandlePaste = useCallback((event: KeyboardEvent) => {
    const eventDocument =
      (event.target as Node | null)?.ownerDocument ?? document;

    return (
      (event.ctrlKey || event.metaKey) &&
      event.key.toLowerCase() === 'v' &&
      !isEditableElement(eventDocument.activeElement)
    );
  }, []);

  const handleCopy = useCallback(() => {
    onCopy();
  }, [onCopy]);

  const handlePaste = useCallback(() => {
    onPaste();
  }, [onPaste]);

  useKeyboardShortcut({
    id: 'layout-editor-copy-object',
    scopeRef,
    shouldHandle: shouldHandleCopy,
    onTrigger: handleCopy,
  });

  useKeyboardShortcut({
    id: 'layout-editor-paste-object',
    scopeRef,
    shouldHandle: shouldHandlePaste,
    onTrigger: handlePaste,
  });
};
